import {
  Component,
  Injector,
  OnInit,
  input,
  output,
  signal,
  computed,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, FormsModule, ValidationErrors, Validator, Validators } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';

type ClockMode = 'hours' | 'minutes';
type AmPm = 'AM' | 'PM';

export interface SoTimeValue {
  hours: number;
  minutes: number;
  ampm?: AmPm;
}

interface OverlayPosition {
  top: number;
  left: number;
  width: number;
}

@Component({
  selector: 'so-time-picker-ui',
  standalone: true,
  imports: [CommonModule, FormsModule, A11yModule, SoButtonGradientUI],
  templateUrl: './so-time-picker.component.html',
  styleUrl: './so-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoTimePickerUI),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SoTimePickerUI),
      multi: true
    }
  ]
})
export class SoTimePickerUI implements ControlValueAccessor, Validator, OnInit {
  // Math reference for template
  protected Math = Math;
  
  // Inputs
  label = input<string>('Seleccionar hora');
  placeholder = input<string>('Seleccionar');
  required = input<boolean>(false);
  use24Hours = input<boolean>(false);
  selectDisplay = input<'vertical' | 'horizontal'>('vertical');
  formStyle = input<boolean>(false);
  triggerOffset = input<number>(8);

  // Outputs
  timeChange = output<SoTimeValue>();
  opened = output<void>();
  closed = output<void>();
  applied = output<SoTimeValue>();
  cancelled = output<void>();

  // Backup values for cancel
  private backupHours = 0;
  private backupMinutes = 0;
  private backupAmPm: AmPm = 'AM';

  // View refs
  @ViewChild('clockFace') clockFaceRef?: ElementRef<SVGElement>;
  @ViewChild('overlayElement') overlayElement?: ElementRef<HTMLDivElement>;
  @ViewChild('hoursInput') hoursInput?: ElementRef<HTMLInputElement>;

  // Internal state
  protected isOpen = signal<boolean>(false);
  protected position = signal<OverlayPosition | null>(null);
  protected clockMode = signal<ClockMode>('hours');
  protected selectedHours = signal<number>(0);
  protected selectedMinutes = signal<number>(0);
  protected selectedAmPm = signal<AmPm>('AM');
  protected isDisabled = signal<boolean>(false);
  protected isDragging = signal<boolean>(false);
  protected isEmpty = signal<boolean>(true);
  protected manualHours = signal<string | null>(null);
  protected manualMinutes = signal<string | null>(null);
  protected isHoursInputInvalid = computed(() => this.manualHours() !== null);
  protected isMinutesInputInvalid = computed(() => this.manualMinutes() !== null);
  protected hasManualInputError = computed(() => this.isHoursInputInvalid() || this.isMinutesInputInvalid());
  private backupIsEmpty = true;
  private lastTriggerRect: DOMRect | null = null;
  private triggerElement: HTMLElement | null = null;
  private touched = false;

  // Computed values
  protected displayHours = computed(() => {
    const hours = this.selectedHours() ?? 0;
    const display = hours % 12 || 12;
    return display.toString().padStart(2, '0');
  });

  protected displayMinutes = computed(() => {
    const minutes = this.selectedMinutes() ?? 0;
    return minutes.toString().padStart(2, '0');
  });

  protected selectedText = computed(() => {
    if (this.isEmpty()) return '';
    const h = this.displayHours();
    const m = this.displayMinutes();
    return `${h}:${m} ${this.selectedAmPm()}`;
  });

  protected hourNumbers = computed(() => {
    // Always show 12 numbers on the clock face (1-12)
    return Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);
  });

  protected minuteNumbers = computed(() => {
    return Array.from({ length: 12 }, (_, i) => i * 5);
  });

  protected handRotation = computed(() => {
    if (this.clockMode() === 'hours') {
      const hours = this.selectedHours() ?? 0;
      return (hours % 12) * 30;
    }
    return (this.selectedMinutes() ?? 0) * 6;
  });

  protected currentTimeValue = computed<SoTimeValue>(() => ({
    hours: this.selectedHours(),
    minutes: this.selectedMinutes(),
    ampm: this.selectedAmPm()
  }));

  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.controlRequired());

  // ControlValueAccessor
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  writeValue(value: SoTimeValue | string | null): void {
    this.clearManualInputErrors();
    if (!value) {
      this.isEmpty.set(true);
      this.selectedHours.set(0);
      this.selectedMinutes.set(0);
      this.selectedAmPm.set('AM');
      return;
    }

    this.isEmpty.set(false);

    if (typeof value === 'string') {
      const parsed = this.parseTimeString(value);
      if (parsed) {
        this.selectedHours.set(parsed.hours);
        this.selectedMinutes.set(parsed.minutes);
        if (parsed.ampm) this.selectedAmPm.set(parsed.ampm);
        else this.selectedAmPm.set(parsed.hours >= 12 ? 'PM' : 'AM');
      }
    } else {
      this.selectedHours.set(value.hours);
      this.selectedMinutes.set(value.minutes);
      if (value.ampm) {
        this.selectedAmPm.set(value.ampm);
      } else {
        this.selectedAmPm.set(value.hours >= 12 ? 'PM' : 'AM');
      }
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    return this.hasManualInputError() ? { invalidTime: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // Methods
  protected onHoursInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseManualPart(input.value, 1, 12);
    if (value === null) {
      this.manualHours.set(input.value);
      this.notifyManualInputValidation();
      return;
    }
    this.manualHours.set(null);
    this.notifyManualInputValidation();

    let nextHours = value % 12;
    if (this.selectedAmPm() === 'PM') {
      nextHours = nextHours === 0 ? 12 : nextHours + 12;
    }

    this.selectedHours.set(nextHours);
    this.emitChange();
  }

  protected onMinutesInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseManualPart(input.value, 0, 59);
    if (value === null) {
      this.manualMinutes.set(input.value);
      this.notifyManualInputValidation();
      return;
    }
    this.manualMinutes.set(null);
    this.notifyManualInputValidation();
    
    this.selectedMinutes.set(value);
    this.emitChange();
  }

  protected onTimeInputKeydown(event: KeyboardEvent, part: ClockMode): void {
    if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const delta = event.key === 'ArrowUp' ? 1 : -1;
    if (part === 'hours') {
      const hours = (this.selectedHours() + delta + 24) % 24;
      this.selectedHours.set(hours);
      this.selectedAmPm.set(hours >= 12 ? 'PM' : 'AM');
      this.manualHours.set(null);
    } else {
      this.selectedMinutes.set((this.selectedMinutes() + delta + 60) % 60);
      this.manualMinutes.set(null);
    }
    this.notifyManualInputValidation();
    this.emitChange();
  }

  protected setAmPm(value: AmPm): void {
    if (this.isDisabled()) return;
    if (this.selectedAmPm() === value) return;

    let nextHours = this.selectedHours();
    if (value === 'PM' && nextHours < 12) {
      nextHours = nextHours + 12;
    } else if (value === 'AM' && nextHours >= 12) {
      nextHours = nextHours - 12;
    }

    this.selectedAmPm.set(value);
    this.selectedHours.set(nextHours);
    this.emitChange();
  }

  protected selectHour(hour: number): void {
    if (this.isDisabled()) return;

    const base = hour % 12; // 12 -> 0
    const nextHours = this.selectedAmPm() === 'PM' ? (base === 0 ? 12 : base + 12) : base;

    this.selectedHours.set(nextHours);
    this.manualHours.set(null);
    this.notifyManualInputValidation();
    this.emitChange();
    
    // Auto switch to minutes after selecting hour
    setTimeout(() => {
      this.clockMode.set('minutes');
    }, 200);
  }

  protected selectMinute(minute: number): void {
    if (this.isDisabled()) return;
    this.selectedMinutes.set(minute);
    this.manualMinutes.set(null);
    this.notifyManualInputValidation();
    this.emitChange();
  }

  protected setClockMode(mode: ClockMode): void {
    this.clockMode.set(mode);
  }

  protected onClockClick(event: MouseEvent): void {
    if (this.isDisabled()) return;
    this.updateFromMouseEvent(event);
  }

  protected onClockMouseDown(event: MouseEvent): void {
    if (this.isDisabled()) return;
    this.isDragging.set(true);
    this.updateFromMouseEvent(event);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging()) return;
    this.updateFromMouseEvent(event);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isDragging()) {
      this.isDragging.set(false);
      if (this.clockMode() === 'hours') {
        setTimeout(() => this.clockMode.set('minutes'), 200);
      }
    }
  }

  private updateFromMouseEvent(event: MouseEvent): void {
    const clockFace = this.clockFaceRef?.nativeElement;
    if (!clockFace) return;

    const rect = clockFace.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const x = event.clientX - centerX;
    const y = event.clientY - centerY;
    
    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;

    if (this.clockMode() === 'hours') {
      let hour = Math.round(angle / 30);
      if (hour === 0) hour = 12;
      if (hour > 12) hour = hour - 12;
      
      const base = hour % 12;
      const nextHours = this.selectedAmPm() === 'PM' ? (base === 0 ? 12 : base + 12) : base;
      this.selectedHours.set(nextHours);
      this.manualHours.set(null);
      this.notifyManualInputValidation();
    } else {
      let minute = Math.round(angle / 6);
      if (minute === 60) minute = 0;
      this.selectedMinutes.set(minute);
      this.manualMinutes.set(null);
      this.notifyManualInputValidation();
    }
    
    this.emitChange();
  }

  protected getNumberPosition(value: number, total: number): { x: number; y: number } {
    const angle = (value * (360 / total) - 90) * (Math.PI / 180);
    const radius = 90;
    return {
      x: 110 + radius * Math.cos(angle),
      y: 110 + radius * Math.sin(angle)
    };
  }

  protected getHourPosition(hour: number): { x: number; y: number } {
    const displayHour = hour === 12 ? 0 : hour;
    const angle = (displayHour * 30 - 90) * (Math.PI / 180);
    const radius = 85;
    return {
      x: 110 + radius * Math.cos(angle),
      y: 110 + radius * Math.sin(angle)
    };
  }

  protected getMinutePosition(minute: number): { x: number; y: number } {
    const angle = (minute * 6 - 90) * (Math.PI / 180);
    const radius = 85;
    return {
      x: 110 + radius * Math.cos(angle),
      y: 110 + radius * Math.sin(angle)
    };
  }

  protected isSelectedHour(hour: number): boolean {
    const selectedDisplay = this.selectedHours() % 12 || 12;
    return selectedDisplay === hour;
  }

  protected isSelectedMinute(minute: number): boolean {
    return this.selectedMinutes() === minute;
  }

  // Trigger methods
  protected onTriggerClick(event: MouseEvent): void {
    if (this.isDisabled()) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement | null;
    this.toggleOverlay(target);
    this.markAsTouched();
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) return;
    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.onClose();
      return;
    }
    if (!['Enter', ' ', 'Spacebar', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    this.toggleOverlay(event.currentTarget as HTMLElement | null);
    this.markAsTouched();
  }

  protected onClose(): void {
    if (!this.isOpen()) return;
    this.selectedHours.set(this.backupHours);
    this.selectedMinutes.set(this.backupMinutes);
    this.selectedAmPm.set(this.backupAmPm);
    this.isEmpty.set(this.backupIsEmpty);
    this.clearManualInputErrors();
    this.onChange(this.backupIsEmpty ? null : this.formatTimeString(this.currentTimeValue()));
    this.closeOverlay();
  }

  private toggleOverlay(target: HTMLElement | null): void {
    if (this.isOpen()) {
      this.closeOverlay();
      return;
    }
    this.openOverlay(target);
  }

  private openOverlay(target: HTMLElement | null): void {
    if (!target) return;
    this.triggerElement = target;
    
    // Save backup values for cancel
    this.backupHours = this.selectedHours();
    this.backupMinutes = this.selectedMinutes();
    this.backupAmPm = this.selectedAmPm();
    this.backupIsEmpty = this.isEmpty();

    // Give the user a helpful starting point: the current time, not midnight
    if (this.isEmpty()) {
      const now = new Date();
      this.selectedHours.set(now.getHours());
      this.selectedMinutes.set(now.getMinutes());
      this.selectedAmPm.set(now.getHours() >= 12 ? 'PM' : 'AM');
    }
    // Normalize defaults to avoid NaN / misaligned hand on first open
    if (Number.isNaN(this.selectedHours())) this.selectedHours.set(0);
    if (Number.isNaN(this.selectedMinutes())) this.selectedMinutes.set(0);
    this.selectedAmPm.set(this.selectedAmPm() || (this.selectedHours() >= 12 ? 'PM' : 'AM'));
    this.clockMode.set('hours');
    this.isDragging.set(false);
    
    const rect = target.getBoundingClientRect();
    this.lastTriggerRect = rect;
    this.position.set({
      top: rect.bottom + this.triggerOffset(),
      left: rect.left,
      width: rect.width
    });
    this.isOpen.set(true);
    this.opened.emit();
    queueMicrotask(() => this.hoursInput?.nativeElement.focus());

    setTimeout(() => {
      if (!this.isOpen() || !this.lastTriggerRect) return;
      this.recalculateOverlayPosition(this.lastTriggerRect);
    });
  }
  
  protected onApply(): void {
    this.emitChange();
    this.applied.emit(this.currentTimeValue());
    this.closeOverlay();
  }

  protected onCancel(): void {
    // Restore backup values
    this.selectedHours.set(this.backupHours);
    this.selectedMinutes.set(this.backupMinutes);
    this.selectedAmPm.set(this.backupAmPm);
    this.isEmpty.set(this.backupIsEmpty);
    this.clearManualInputErrors();
    this.onChange(this.backupIsEmpty ? null : this.formatTimeString(this.currentTimeValue()));
    this.cancelled.emit();
    this.closeOverlay();
  }

  private recalculateOverlayPosition(triggerRect: DOMRect): void {
    const overlayRef = this.overlayElement?.nativeElement;
    if (!overlayRef) return;

    const overlayRect = overlayRef.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const availableBelow = viewportHeight - triggerRect.bottom;
    const availableAbove = triggerRect.top;
    const overlayTotalHeight = overlayRect.height + this.triggerOffset();

    let top = triggerRect.bottom + this.triggerOffset();
    if (availableBelow < overlayTotalHeight && availableAbove >= overlayTotalHeight) {
      top = triggerRect.top - overlayRect.height - this.triggerOffset();
    } else if (availableBelow < overlayTotalHeight) {
      const maxTop = Math.max(this.triggerOffset(), viewportHeight - overlayRect.height - this.triggerOffset());
      top = Math.min(top, maxTop);
    }

    const overlayTotalWidth = overlayRect.width;
    let left = triggerRect.left;
    if (left + overlayTotalWidth > viewportWidth - this.triggerOffset()) {
      left = Math.max(this.triggerOffset(), viewportWidth - overlayTotalWidth - this.triggerOffset());
    }

    this.position.set({ top, left, width: triggerRect.width });
  }

  private closeOverlay(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.closed.emit();
    queueMicrotask(() => this.triggerElement?.focus());
  }

  private markAsTouched(): void {
    if (this.touched) return;
    this.onTouched();
    this.touched = true;
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    this.onClose();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.onClose();
  }

  private emitChange(): void {
    if (this.hasManualInputError()) return;
    this.isEmpty.set(false);
    const value = this.currentTimeValue();
    this.onChange(this.formatTimeString(value));
    this.timeChange.emit(value);
    this.onTouched();
  }

  private parseManualPart(value: string, min: number, max: number): number | null {
    if (!/^\d{1,2}$/.test(value)) return null;
    const parsed = Number(value);
    return parsed >= min && parsed <= max ? parsed : null;
  }

  private notifyManualInputValidation(): void {
    this.onValidatorChange();
    this.onTouched();
  }

  private clearManualInputErrors(): void {
    if (this.manualHours() === null && this.manualMinutes() === null) return;
    this.manualHours.set(null);
    this.manualMinutes.set(null);
    this.onValidatorChange();
  }

  private parseTimeString(time: string): SoTimeValue | null {
    // Parse formats: "HH:mm", "HH:mm AM/PM", "HH:mm:ss"
    const match = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[4]?.toUpperCase() as AmPm | undefined;

    if (ampm === 'PM') {
      hours = hours % 12 + 12; // 12 PM stays 12
    } else if (ampm === 'AM') {
      hours = hours % 12; // 12 AM becomes 0
    }

    return {
      hours,
      minutes,
      ampm: ampm || (hours >= 12 ? 'PM' : 'AM')
    };
  }

  private formatTimeString(value: SoTimeValue): string {
    const hours24 = value.hours;
    const minutes = value.minutes.toString().padStart(2, '0');
    const displayHour = hours24 % 12 || 12;
    const ampm = value.ampm || (hours24 >= 12 ? 'PM' : 'AM');
    return `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  }
}
